import React, { useState, useEffect } from 'react';
import { generateExamV2Questions } from '../utils/examV2Utils';
import { generateExamQuestions } from '../utils/examUtils';
import { saveExamProgress, loadExamProgress, clearExamProgress } from '../utils/examStorage';

const MockExam = ({ onExamComplete, shouldResumeProgress = false, examVersion = 'v1' }) => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [examStarted, setExamStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // '', 'saving', 'saved', 'error'

  // Auto-save progress when state changes
  useEffect(() => {
    if (examStarted && questions.length > 0) {
      const saveState = {
        questions,
        selectedAnswers,
        currentQuestion,
        examStarted: true
      };

      setSaveStatus('saving');
      const saved = saveExamProgress(saveState, examVersion);
      
      if (saved) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2000); // Clear status after 2 seconds
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    }
  }, [selectedAnswers, currentQuestion, questions, examStarted, examVersion]);

  // Load saved progress or initialize new exam on component mount
  useEffect(() => {
    if (shouldResumeProgress) {
      const savedProgress = loadExamProgress(examVersion);
      if (savedProgress) {
        setQuestions(savedProgress.questions);
        setSelectedAnswers(savedProgress.selectedAnswers);
        setCurrentQuestion(savedProgress.currentQuestion);
        setExamStarted(true);
      }
    } else {
      // Auto-initialize exam when not resuming
      initializeExam();
    }
  }, [shouldResumeProgress, examVersion]);

  const initializeExam = async () => {
    setIsLoading(true);
    try {
      const examQuestions = examVersion === 'v2' ? generateExamV2Questions() : generateExamQuestions();
      setQuestions(examQuestions);
      setCurrentQuestion(0);
      setSelectedAnswers({});
      setExamStarted(true);
    } catch (error) {
      console.error('Error generating exam questions:', error);
    }
    setIsLoading(false);
  };

  const handleAnswerSelect = (examId, answerIndex) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [examId]: answerIndex
    });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Exam completed - keep saved progress for review
      onExamComplete(questions, selectedAnswers);
    }
  };

  const handleFinishExam = () => {
    const answeredCount = getAnsweredCount();
    const unansweredCount = questions.length - answeredCount;
    
    if (unansweredCount > 0) {
      const confirmed = window.confirm(
        `Are you sure you want to finish the exam?\n\n` +
        `You have answered ${answeredCount} out of ${questions.length} questions.\n` +
        `${unansweredCount} questions will be marked as incorrect.\n\n` +
        `Click OK to finish the exam or Cancel to continue.`
      );
      
      if (!confirmed) {
        return;
      }
    } else {
      const confirmed = window.confirm(
        `You have answered all ${questions.length} questions.\n\n` +
        `Are you ready to finish the exam and see your results?`
      );
      
      if (!confirmed) {
        return;
      }
    }
    
    onExamComplete(questions, selectedAnswers);
  };

  const handleResetProgress = () => {
    if (window.confirm('Are you sure you want to reset your progress? This will clear all your answers and start a new exam.')) {
      clearExamProgress(examVersion);
      setQuestions([]);
      setSelectedAnswers({});
      setCurrentQuestion(0);
      setExamStarted(false);
      setSaveStatus('');
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleQuestionJump = (questionIndex) => {
    setCurrentQuestion(questionIndex);
  };

  const getAnsweredCount = () => {
    return Object.keys(selectedAnswers).length;
  };

  const getCurrentSection = () => {
    return currentQuestion < 40 ? 'regulations' : 'technical';
  };

  const getSectionProgress = () => {
    if (currentQuestion < 40) {
      return { section: 'Regulations', current: currentQuestion + 1, total: 40 };
    } else {
      return { section: 'Technical', current: currentQuestion - 39, total: 60 };
    }
  };


  if (questions.length === 0 || isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <p className="text-gray-600">Loading exam questions...</p>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const selectedAnswer = selectedAnswers[question.examId];
  const sectionProgress = getSectionProgress();

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header with progress */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Mock RAE Exam{examVersion === 'v2' ? ' V2' : ''}
            </h2>
            <p className="text-sm text-gray-600">
              {sectionProgress.section} Section - Question {sectionProgress.current} of {sectionProgress.total}
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">
              Question {currentQuestion + 1} of 100
            </div>
            <div className="text-sm text-gray-500">
              Answered: {getAnsweredCount()}/100
            </div>
            <div className={`text-xs mt-1 h-4 ${
              saveStatus === 'saved' ? 'text-green-600' : 
              saveStatus === 'saving' ? 'text-blue-600' : 
              saveStatus === 'error' ? 'text-red-600' : 'text-transparent'
            }`}>
              {saveStatus === 'saved' && '✓ Progress saved'}
              {saveStatus === 'saving' && '⟳ Saving...'}
              {saveStatus === 'error' && '⚠ Save failed'}
              {!saveStatus && '·'} {/* Invisible placeholder */}
            </div>
          </div>
        </div>
        
        {/* Overall progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestion + 1) / 100) * 100}%` }}
          />
        </div>
        
        {/* Section indicator */}
        <div className="flex text-xs text-gray-500">
          <span className={`flex-1 text-center py-1 ${getCurrentSection() === 'regulations' ? 'font-semibold text-blue-600' : ''}`}>
            Regulations (1-40)
          </span>
          <span className={`flex-1 text-center py-1 ${getCurrentSection() === 'technical' ? 'font-semibold text-blue-600' : ''}`}>
            Technical (41-100)
          </span>
        </div>
        
      </div>

      {/* Question content */}
      <div className="p-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          {question.question}
        </h3>

        <div className="space-y-3 mb-8">
          {question.options.map((option, index) => {
            const isSelected = selectedAnswer === index;
            
            return (
              <label 
                key={index}
                className={`block p-4 border rounded-lg transition-colors cursor-pointer ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name={`question-${question.examId}`}
                  value={index}
                  checked={isSelected}
                  onChange={() => handleAnswerSelect(question.examId, index)}
                  className="sr-only"
                />
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">{option}</span>
                  {isSelected && (
                    <span className="text-blue-600 font-semibold">Selected</span>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
          <button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="flex items-center justify-center px-4 py-3 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors touch-manipulation w-full sm:w-auto order-1 sm:order-none"
          >
            <span className="mr-1">←</span> Previous
          </button>

          {/* Question Navigation */}
          <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full sm:w-auto order-1 sm:order-none">
            {/* Jump to Question Dropdown */}
            <div className="relative order-2 sm:order-none">
              <select
                value={currentQuestion}
                onChange={(e) => handleQuestionJump(parseInt(e.target.value))}
                className="text-sm px-3 py-2 border border-gray-300 rounded bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-w-[120px]"
                title="Jump to any question"
              >
                <optgroup label="📋 Regulations (1-40)">
                  {questions.slice(0, 40).map((_, index) => {
                    const isAnswered = selectedAnswers[questions[index]?.examId] !== undefined;
                    return (
                      <option key={index} value={index}>
                        Q{index + 1} {isAnswered ? '✓' : '○'}
                      </option>
                    );
                  })}
                </optgroup>
                <optgroup label="⚡ Technical (41-100)">
                  {questions.slice(40, 100).map((_, index) => {
                    const questionIndex = index + 40;
                    const isAnswered = selectedAnswers[questions[questionIndex]?.examId] !== undefined;
                    return (
                      <option key={questionIndex} value={questionIndex}>
                        Q{questionIndex + 1} {isAnswered ? '✓' : '○'}
                      </option>
                    );
                  })}
                </optgroup>
              </select>
            </div>

            {/* Question navigation dots - responsive display */}
            <div className="flex flex-col items-center space-y-2 order-1 sm:order-none">
              {/* Mobile view: show 4 dots with better spacing and sizing */}
              <div className="flex space-x-1.5 sm:hidden justify-center w-full">
              {(() => {
                // Mobile: show only 4 questions to fit better on screen with larger touch targets
                const maxQuestionsToShow = 4;
                const halfRange = Math.floor(maxQuestionsToShow / 2);
                let startIndex = Math.max(0, currentQuestion - halfRange);
                let endIndex = Math.min(questions.length, startIndex + maxQuestionsToShow);
                
                if (endIndex - startIndex < maxQuestionsToShow) {
                  startIndex = Math.max(0, endIndex - maxQuestionsToShow);
                }
                
                const questionsToShow = [];
                for (let i = startIndex; i < endIndex; i++) {
                  const isAnswered = selectedAnswers[questions[i]?.examId] !== undefined;
                  const isCurrent = i === currentQuestion;
                  const isRegulationSection = i < 40;
                  
                  questionsToShow.push(
                    <button
                      key={i}
                      onClick={() => handleQuestionJump(i)}
                      className={`w-9 h-9 rounded-full text-sm font-medium transition-colors touch-manipulation flex-shrink-0 shadow-sm ${
                        isCurrent
                          ? 'bg-blue-600 text-white'
                          : isAnswered
                          ? 'bg-green-100 text-green-700 border border-green-300 active:bg-green-300'
                          : 'bg-gray-100 text-gray-600 border border-gray-300 active:bg-gray-300'
                      } ${
                        i === 40 ? 'ml-1' : ''
                      }`}
                      title={`Question ${i + 1} (${isRegulationSection ? 'Regulations' : 'Technical'})`}
                    >
                      {i + 1}
                    </button>
                  );
                }
                
                return questionsToShow;
              })()}
              </div>

              {/* Desktop view: show 8 dots with better spacing */}
              <div className="hidden sm:flex space-x-2">
              {(() => {
                // Desktop: show 8 questions centered around current for better spacing
                const maxQuestionsToShow = 8;
                const halfRange = Math.floor(maxQuestionsToShow / 2);
                let startIndex = Math.max(0, currentQuestion - halfRange);
                let endIndex = Math.min(questions.length, startIndex + maxQuestionsToShow);
                
                if (endIndex - startIndex < maxQuestionsToShow) {
                  startIndex = Math.max(0, endIndex - maxQuestionsToShow);
                }
                
                const questionsToShow = [];
                for (let i = startIndex; i < endIndex; i++) {
                  const isAnswered = selectedAnswers[questions[i]?.examId] !== undefined;
                  const isCurrent = i === currentQuestion;
                  const isRegulationSection = i < 40;
                  
                  questionsToShow.push(
                    <button
                      key={i}
                      onClick={() => handleQuestionJump(i)}
                      className={`w-8 h-8 rounded-full text-sm font-medium transition-colors shadow-sm ${
                        isCurrent
                          ? 'bg-blue-600 text-white'
                          : isAnswered
                          ? 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                      } ${
                        i === 40 ? 'ml-2' : ''
                      }`}
                      title={`Question ${i + 1} (${isRegulationSection ? 'Regulations' : 'Technical'})`}
                    >
                      {i + 1}
                    </button>
                  );
                }
                
                return questionsToShow;
              })()}
              </div>
              
              {/* Show current visible range */}
              <div className="text-xs text-gray-400 text-center">
                {(() => {
                  // Mobile range calculation (4 dots)
                  const mobileMax = 4;
                  const mobileHalf = Math.floor(mobileMax / 2);
                  let mobileStart = Math.max(0, currentQuestion - mobileHalf);
                  let mobileEnd = Math.min(questions.length, mobileStart + mobileMax);
                  if (mobileEnd - mobileStart < mobileMax) {
                    mobileStart = Math.max(0, mobileEnd - mobileMax);
                  }
                  
                  // Desktop range calculation (8 dots)
                  const desktopMax = 8;
                  const desktopHalf = Math.floor(desktopMax / 2);
                  let desktopStart = Math.max(0, currentQuestion - desktopHalf);
                  let desktopEnd = Math.min(questions.length, desktopStart + desktopMax);
                  if (desktopEnd - desktopStart < desktopMax) {
                    desktopStart = Math.max(0, desktopEnd - desktopMax);
                  }
                  
                  return (
                    <>
                      <span className="sm:hidden">Showing {mobileStart + 1}-{mobileEnd}</span>
                      <span className="hidden sm:inline">Showing {desktopStart + 1}-{desktopEnd}</span>
                    </>
                  );
                })()}
              </div>
            </div>
            
            {/* Show section boundary indicator */}
            {(() => {
              // Check if section boundary (question 40) is in visible range for mobile (4 dots)
              const mobileMax = 4;
              const mobileHalf = Math.floor(mobileMax / 2);
              let mobileStart = Math.max(0, currentQuestion - mobileHalf);
              let mobileEnd = Math.min(questions.length, mobileStart + mobileMax);
              if (mobileEnd - mobileStart < mobileMax) {
                mobileStart = Math.max(0, mobileEnd - mobileMax);
              }
              const mobileShowsBoundary = mobileStart < 40 && mobileEnd > 40;
              
              // Check if section boundary (question 40) is in visible range for desktop (8 dots)
              const desktopMax = 8;
              const desktopHalf = Math.floor(desktopMax / 2);
              let desktopStart = Math.max(0, currentQuestion - desktopHalf);
              let desktopEnd = Math.min(questions.length, desktopStart + desktopMax);
              if (desktopEnd - desktopStart < desktopMax) {
                desktopStart = Math.max(0, desktopEnd - desktopMax);
              }
              const desktopShowsBoundary = desktopStart < 40 && desktopEnd > 40;
              
              if (mobileShowsBoundary || desktopShowsBoundary) {
                return (
                  <div className="text-xs text-gray-500 px-2 text-center">
                    <span className="text-blue-600">Reg</span> | <span className="text-green-600">Tech</span>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto order-2 sm:order-none">
            <button
              onClick={handleFinishExam}
              className="flex items-center justify-center px-4 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors touch-manipulation w-full sm:w-auto font-medium"
              title="Finish exam and see results"
            >
              Finish Exam <span className="ml-1">🎯</span>
            </button>

            <button
              onClick={handleNext}
              disabled={currentQuestion === questions.length - 1}
              className={`flex items-center justify-center px-6 py-3 rounded-md transition-colors touch-manipulation w-full sm:w-auto font-medium ${
                currentQuestion === questions.length - 1
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {currentQuestion === questions.length - 1 ? (
                <>Last Question <span className="ml-1">⏹</span></>
              ) : (
                <>Next <span className="ml-1">→</span></>
              )}
            </button>
          </div>
        </div>

        {/* Section transition indicator */}
        {currentQuestion === 39 && (
          <div className="mt-4 p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-sm font-medium text-center sm:text-left">
              ⚠️ You're about to enter the Technical section (Questions 41-100)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MockExam;